import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient } from "../App";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Loader2, Droplet, RefreshCw, ArrowRight } from "lucide-react";

const CONFIDENCE_TONE = {
  High: "bg-emerald-100 text-emerald-800 border-emerald-200",
  Medium: "bg-amber-100 text-amber-800 border-amber-200",
  Low: "bg-slate-100 text-slate-700 border-slate-200",
};

export default function RevenueLeaks({ onNavigate, onOpenFundraiser }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get("/revenue-leaks");
      setData(res.data);
    } catch (e) {
      if (e?.response?.status === 403) setData({ forbidden: true });
      else setData({ error: e?.response?.data?.detail || "Failed to load" });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleCta = (leak) => {
    const cta = leak.cta || {};
    if (cta.fundraiserId) {
      if (onOpenFundraiser) onOpenFundraiser(cta.fundraiserId);
      else navigate(`/owner/fundraiser/${cta.fundraiserId}`);
      return;
    }
    if (cta.tab && onNavigate) onNavigate(cta.tab);
  };

  if (loading) {
    return (
      <Card className="p-4 border-slate-200" data-testid="revenue-leaks-loading">
        <div className="flex items-center gap-2 text-slate-500 text-sm">
          <Loader2 className="h-4 w-4 animate-spin" /> Scanning for leaks…
        </div>
      </Card>
    );
  }
  if (!data || data.forbidden || data.error) return null;

  const rows = (data.leaks || []).slice(0, 5);
  const fmtGBP = (n) => `£${Math.round(n || 0).toLocaleString("en-GB")}`;

  return (
    <Card className="p-4 border-slate-200 bg-white" data-testid="revenue-leaks">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Droplet className="h-4 w-4 text-rose-500" />
          <h3 className="font-semibold text-slate-900 text-sm">Revenue Leaks</h3>
          <span className="text-xs text-slate-500">
            {rows.length} detected · {fmtGBP(data.totalImpactGBP)} at risk
          </span>
        </div>
        <Button variant="ghost" size="sm" onClick={load} className="h-7 px-2" data-testid="revenue-leaks-refresh">
          <RefreshCw className="h-3.5 w-3.5" />
        </Button>
      </div>
      {rows.length === 0 ? (
        <div className="text-xs text-slate-500 py-4 text-center" data-testid="revenue-leaks-empty">
          No leaks detected. Revenue pipeline is clean.
        </div>
      ) : (
        <div className="space-y-1.5" data-testid="revenue-leaks-list">
          {rows.map((l, i) => (
            <div
              key={l.type + i}
              className="flex items-start gap-3 p-2.5 rounded-md bg-slate-50 border border-slate-100"
              data-testid={`revenue-leak-${l.type}`}
            >
              <span className="text-[10px] font-mono text-slate-400 w-4 pt-0.5">#{i + 1}</span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-0.5">
                  <Badge className={`border text-[10px] px-1.5 py-0 ${CONFIDENCE_TONE[l.confidence] || CONFIDENCE_TONE.Low}`}>
                    {l.confidence}
                  </Badge>
                  <span className="text-[11px] font-mono text-rose-700 font-semibold">{fmtGBP(l.estImpactGBP)}</span>
                </div>
                <div className="text-sm font-medium text-slate-900 leading-snug">{l.title}</div>
                <div className="text-xs text-slate-500 mt-0.5">{l.description}</div>
              </div>
              {l.cta?.label && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleCta(l)}
                  className="flex-shrink-0 h-7 text-[11px] text-indigo-700 hover:bg-indigo-50 gap-1"
                  data-testid={`revenue-leak-cta-${l.type}`}
                >
                  {l.cta.label}
                  <ArrowRight className="h-3 w-3" />
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
